import type {
  CurrentSubscriptionContract,
  PendingChargeContract,
} from '../contracts/current-subscription.contract';
import type { InvoiceContract } from '../contracts/invoice.contract';
import type { UsageContract, UsageResourceContract } from '../contracts/usage.contract';
import type {
  InvoiceStatus,
  InvoiceSummaryModel,
  PendingChargeModel,
  SubscriptionModel,
  UsageModel,
  UsageResourceModel,
} from '../models/billing.models';
import type {
  BillingCycle,
  PaymentMethod,
  PlanSlug,
  PlanTier,
  SubscriptionStatus,
} from '../models/subscription-status.enum';
import { daysBetween } from '../utils/trial-progression.util';

const KNOWN_STATUSES: readonly SubscriptionStatus[] = [
  'Trialing',
  'Active',
  'PastDue',
  'Canceled',
  'Expired',
  'Pending',
] as const;

const KNOWN_PLANS: readonly PlanSlug[] = ['starter', 'pro', 'business'] as const;

export function mapCurrentSubscriptionContract(
  raw: CurrentSubscriptionContract | null | undefined
): SubscriptionModel | null {
  if (!raw || typeof raw !== 'object') return null;

  const status = normalizeStatus(raw.status);
  const planSlug = normalizePlanSlug(raw.planSlug);
  const planTier = normalizePlanTier(raw.planTier, planSlug);

  const daysRemaining =
    typeof raw.daysRemaining === 'number' && Number.isFinite(raw.daysRemaining)
      ? Math.max(0, Math.floor(raw.daysRemaining))
      : resolveDaysRemainingFallback(status, raw.trialEndsAt, raw.currentPeriodEnd);

  return {
    subscriptionId: trimOrNull(raw.subscriptionId),
    status,
    planSlug,
    planTier,
    planName: (raw.planName ?? planTier ?? '').toString().trim() || (planTier ?? ''),
    billingCycle: normalizeBillingCycle(raw.billingCycle),
    paymentMethod: normalizePaymentMethod(raw.paymentMethod),
    currentPeriodStart: trimOrNull(raw.currentPeriodStart),
    currentPeriodEnd: trimOrNull(raw.currentPeriodEnd),
    trialEndsAt: trimOrNull(raw.trialEndsAt),
    canceledAt: trimOrNull(raw.canceledAt),
    daysRemaining,
    isBlocked: Boolean(raw.isBlocked),
    blockReason: trimOrNull(raw.blockReason),
    statusMessage: trimOrNull(raw.statusMessage),
    pendingCharge: mapPendingCharge(raw.pendingCharge),
    scheduledPlanChange:
      raw.scheduledPlanChange && typeof raw.scheduledPlanChange === 'object'
        ? {
            planSlug: normalizePlanSlug(raw.scheduledPlanChange.planSlug) ?? 'starter',
            effectiveAt: (raw.scheduledPlanChange.effectiveAt ?? '').toString(),
          }
        : null,
  };
}

export function mapUsageContract(raw: UsageContract | null | undefined): UsageModel | null {
  if (!raw || typeof raw !== 'object') return null;
  const resources: UsageResourceModel[] = [];

  if (Array.isArray(raw.resources)) {
    for (const item of raw.resources) {
      const mapped = mapUsageResource(item);
      if (mapped) resources.push(mapped);
    }
  }

  const flatPairs: [UsageResourceModel['key'], UsageContract[keyof UsageContract]][] = [
    ['admins', raw.admins],
    ['workflows', raw.workflows],
    ['automations', raw.automations],
    ['whatsappMonthly', raw.whatsappMonthly],
    ['forms', raw.forms],
    ['projects', raw.projects],
  ];

  for (const [key, value] of flatPairs) {
    if (!value || typeof value !== 'object') continue;
    if (resources.some((r) => r.key === key)) continue;
    const used = asNumber((value as { used?: number }).used, 0);
    const limit = asNumber((value as { limit?: number }).limit, -1);
    resources.push({
      key,
      used,
      limit,
      isUnlimited: limit < 0,
    });
  }

  return {
    resources,
    updatedAt: trimOrNull(raw.updatedAt),
  };
}

export function mapInvoicesContract(raw: unknown): InvoiceSummaryModel[] {
  if (!Array.isArray(raw)) return [];
  const result: InvoiceSummaryModel[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const source = item as InvoiceContract;
    const invoiceId = (source.id ?? '').toString().trim();
    if (!invoiceId) continue;
    const paidAt = trimOrNull(source.paidAt);
    const dueDate = trimOrNull(source.dueDate);
    const createdAt = trimOrNull(source.createdAt);
    // Data usada em lugar único na UI (ex.: lista): paidAt se existir, senão dueDate, senão createdAt.
    const issuedAt = paidAt ?? dueDate ?? createdAt ?? '';
    result.push({
      invoiceId,
      issuedAt,
      dueDate,
      paidAt,
      amountCents: asNumber(source.amountCents, 0),
      status: resolveInvoiceStatus(source.status),
      paymentMethod: normalizePaymentMethod(source.paymentMethod),
      gatewayExternalId: trimOrNull(source.gatewayExternalId ?? null),
    });
  }
  // Backend já ordena por dueDate DESC; reforça caso venha desordenado ou mock não respeite.
  result.sort((a, b) => {
    const aDate = a.dueDate ?? a.issuedAt ?? '';
    const bDate = b.dueDate ?? b.issuedAt ?? '';
    return bDate.localeCompare(aDate);
  });
  return result;
}

function mapUsageResource(raw: UsageResourceContract | null | undefined): UsageResourceModel | null {
  if (!raw || typeof raw !== 'object') return null;
  const key = normalizeResourceKey(raw.key);
  if (!key) return null;
  const limit = asNumber(raw.limit, -1);
  return {
    key,
    used: asNumber(raw.used, 0),
    limit,
    isUnlimited: Boolean(raw.isUnlimited) || limit < 0,
  };
}

function mapPendingCharge(raw: PendingChargeContract | null | undefined): PendingChargeModel | null {
  if (!raw || typeof raw !== 'object') return null;
  const invoiceId = (raw.invoiceId ?? '').toString().trim();
  if (!invoiceId) return null;
  return {
    invoiceId,
    amountCents: asNumber(raw.amountCents, 0),
    dueDate: (raw.dueDate ?? '').toString(),
    qrCodeBase64: (raw.qrCodeBase64 ?? '').toString(),
    copyPasteCode: (raw.copyPasteCode ?? '').toString(),
    gatewayOrderId: (raw.gatewayOrderId ?? '').toString(),
  };
}

function normalizeStatus(value: string | undefined): SubscriptionStatus | null {
  if (!value) return null;
  const normalized = value.trim();
  return KNOWN_STATUSES.find((s) => s.toLowerCase() === normalized.toLowerCase()) ?? null;
}

function normalizePlanSlug(value: string | undefined): PlanSlug | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  return KNOWN_PLANS.find((s) => s === normalized) ?? null;
}

function normalizePlanTier(
  value: string | undefined,
  fallbackFromSlug: PlanSlug | null
): PlanTier | null {
  const candidate = (value ?? '').trim();
  if (candidate.toLowerCase() === 'starter') return 'Starter';
  if (candidate.toLowerCase() === 'pro') return 'Pro';
  if (candidate.toLowerCase() === 'business') return 'Business';
  if (fallbackFromSlug === 'starter') return 'Starter';
  if (fallbackFromSlug === 'pro') return 'Pro';
  if (fallbackFromSlug === 'business') return 'Business';
  return null;
}

function normalizeBillingCycle(value: string | undefined): BillingCycle | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  if (v === 'monthly' || v === 'month') return 'Monthly';
  if (v === 'annual' || v === 'yearly' || v === 'year') return 'Annual';
  return null;
}

function normalizePaymentMethod(value: string | undefined): PaymentMethod | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  if (v === 'pix') return 'Pix';
  if (v === 'creditcard' || v === 'credit_card' || v === 'card') return 'CreditCard';
  return null;
}

function normalizeResourceKey(value: string | undefined): UsageResourceModel['key'] | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  if (v === 'admins' || v === 'admin') return 'admins';
  if (v === 'workflows' || v === 'workflow') return 'workflows';
  if (v === 'automations' || v === 'automation') return 'automations';
  if (v === 'whatsappmonthly' || v === 'whatsapp' || v === 'whatsapppermonth') return 'whatsappMonthly';
  if (v === 'forms' || v === 'form') return 'forms';
  if (v === 'projects' || v === 'project') return 'projects';
  return null;
}

function resolveInvoiceStatus(value: string | undefined): InvoiceStatus {
  const v = (value ?? '').trim().toLowerCase();
  if (v === 'paid') return 'Paid';
  if (v === 'failed') return 'Failed';
  if (v === 'refunded') return 'Refunded';
  if (v === 'canceled' || v === 'cancelled') return 'Canceled';
  return 'Open';
}

function resolveDaysRemainingFallback(
  status: SubscriptionStatus | null,
  trialEndsAt: string | undefined,
  currentPeriodEnd: string | undefined
): number | null {
  if (status === 'Trialing') return daysBetween(trialEndsAt);
  if (status === 'Active' || status === 'Canceled' || status === 'PastDue') {
    return daysBetween(currentPeriodEnd);
  }
  return null;
}

function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function trimOrNull(value: string | undefined | null): string | null {
  if (!value) return null;
  const trimmed = value.toString().trim();
  return trimmed ? trimmed : null;
}
